import { FloatingElement } from "@data-display/layout/FloatingElement";
import { StackGrid, stackGridClasses } from "@data-display/layout/StackGrid";
import CloseIcon from "@mui/icons-material/Close";
import { Box, FormHelperText, IconButton, MenuItem, TextField } from "@mui/material";
import { useGlobalContext } from "@root/context";
import { useMemo } from "react";
import { type Control, Controller, useFormState, useWatch } from "react-hook-form";
import { time } from "../logics/constants";
import type { IModalActionForm, IModalActionResolver } from "../logics/types";
import { hasStartTimeRequired } from "../logics/utils";

export interface IModalActionFormTimeProps {
  control: Control<IModalActionForm, void, IModalActionResolver>;
}

export const ModalActionFormTime = ({ control }: IModalActionFormTimeProps) => {
  const { user } = useGlobalContext();
  const { errors } = useFormState({ control, name: "startTime" });
  const [type, startTime, endTime, sendEventToOwner, status, shouldScheduleAppointmentReminder] = useWatch({
    control,
    name: ["type", "startTime", "endTime", "sendEventToOwner", "status", "shouldScheduleAppointmentReminder"],
  });
  const startTimeRequired = hasStartTimeRequired(
    type,
    user.organisation.features.action.requiredContactDetails,
    endTime,
    sendEventToOwner,
    status,
    shouldScheduleAppointmentReminder
  );

  const timeEndMemo = useMemo(() => {
    if (startTime) {
      return time.filter((option) => option.value > startTime);
    }
    return time;
  }, [startTime]);

  return (
    <StackGrid perLine={{ tablet: 2 }} direction="row" columnGap={1} strict>
      <Controller
        name="startTime"
        control={control}
        render={({ field: { value, onChange }, fieldState: { error } }) => (
          <TextField
            label={
              <Box
                sx={{
                  pr: 1,
                }}
              >{`Début (h)${startTimeRequired ? " *" : ""}`}</Box>
            }
            value={value}
            onChange={(evt) => onChange(evt.target.value)}
            size="small"
            select
            error={!!error}
            fullWidth
            slotProps={{
              select: {
                // biome-ignore lint/style/useNamingConvention: <explanation>
                MenuProps: {
                  slotProps: {
                    paper: {
                      sx: { maxHeight: "200px" },
                    },
                  },
                },
                ...(value !== "" && {
                  endAdornment: (
                    <FloatingElement
                      position="right"
                      slotProps={{
                        box: { mr: "-6px", right: 34 },
                      }}
                    >
                      <IconButton sx={{ p: "4px" }} onClick={() => onChange("")}>
                        <CloseIcon color="inherit" fontSize="small" />
                      </IconButton>
                    </FloatingElement>
                  ),
                }),
              },
            }}
          >
            {time.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        )}
      />
      <Controller
        name="endTime"
        control={control}
        render={({ field: { value, onChange } }) => (
          <TextField
            label={
              <Box
                sx={{
                  pr: 1,
                }}
              >
                Fin (h)
              </Box>
            }
            value={value ?? ""}
            onChange={(evt) => onChange(evt.target.value)}
            size="small"
            select
            fullWidth
            slotProps={{
              select: {
                // biome-ignore lint/style/useNamingConvention: <explanation>
                MenuProps: {
                  slotProps: {
                    paper: {
                      sx: { maxHeight: "200px" },
                    },
                  },
                },
                ...(value !== "" && {
                  endAdornment: (
                    <FloatingElement
                      position="right"
                      slotProps={{
                        box: { mr: "-6px", right: 34 },
                      }}
                    >
                      <IconButton sx={{ p: "4px" }} onClick={() => onChange("")}>
                        <CloseIcon color="inherit" fontSize="small" />
                      </IconButton>
                    </FloatingElement>
                  ),
                }),
              },
            }}
          >
            {timeEndMemo.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        )}
      />
      {Boolean(errors.startTime) && (
        <FormHelperText error className={stackGridClasses.fullWidth} sx={{ pt: 0.5, px: 1.75 }}>
          {errors.startTime?.message}
        </FormHelperText>
      )}
    </StackGrid>
  );
};
